import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Bug,
  X as XIcon,
  Check,
  Loader2,
  Camera,
  Mic,
  ImagePlus,
  Trash2,
  Copy,
  ChevronRight,
  ChevronLeft,
  Clipboard,
  AlertCircle,
  Sparkles,
  FileText,
  RefreshCw,
  Users,
  User as UserIcon,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { uploadFile, uploadUrl } from "../lib/uploads";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import VoiceTextarea from "./VoiceTextarea";
import MediaLightbox from "./MediaLightbox";
import { toast } from "./ui/sonner";
import useVisualViewportHeight from "../hooks/useVisualViewportHeight";
import DictTextarea from "./DictTextarea";

/**
 * GlobalReportBugButton — Wave 25.6 upgrade.
 *
 * Mounted once in Layout, visible on every authenticated page.
 * Captures: route, free-text reason, voice memo + transcript,
 * screenshots / attachments, and recent console errors.
 * Files to /api/hidro/bugs.  Includes a "My reports" tab so the user
 * can copy any of their previous reports as Markdown to paste to support.
 */
const _consoleErrors = [];
const _origError = console.error;

if (typeof window !== "undefined" && !window.__hidroBugConsolePatched) {
  console.error = (...args) => {
    try {
      const line = args
        .map((a) => (typeof a === "string" ? a : (a?.message || JSON.stringify(a))))
        .join(" ");
      _consoleErrors.push({ line: line.slice(0, 1000), at: new Date().toISOString() });
      if (_consoleErrors.length > 25) _consoleErrors.shift();
    } catch (_e) { /* noop */ }
    return _origError.apply(console, args);
  };
  window.__hidroBugConsolePatched = true;
}

const KIND_OF = (mime) => {
  if (!mime) return "file";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  return "file";
};

export default function GlobalReportBugButton() {
  const { user } = useAuth() || {};
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  const isOwner = user?.role === "owner";

  // Wave 26.10.1 — same visual-viewport handling as Engineering Desk so
  // the soft keyboard doesn't push the composer off-screen on mobile.
  useVisualViewportHeight();

  const [open, setOpen] = useState(false);
  // Tabs: new | mine | briefs (briefs owner-only)
  const [tab, setTab] = useState("new");
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [myBugs, setMyBugs] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  // Wave 26.5 — owner can switch between own reports and the team's full
  // bug stream when picking what to compile.
  const [scope, setScope] = useState("mine"); // mine | team (owner only)
  // Selection set for the multi-select compile flow.
  const [selected, setSelected] = useState(new Set());
  // Compile flow state.
  const [compiling, setCompiling] = useState(false);
  const [compileErr, setCompileErr] = useState("");
  const [compiledBrief, setCompiledBrief] = useState(null); // {id, title, markdown, ...}
  const [includeResolved, setIncludeResolved] = useState(false);
  const [briefCopied, setBriefCopied] = useState(false);
  // Saved briefs.
  const [briefs, setBriefs] = useState([]);
  const [loadingBriefs, setLoadingBriefs] = useState(false);
  // Wave 26.10 — hide resolved + wontfix from the list by default. Only
  // surface them when user explicitly toggles "Show resolved".
  const [showResolved, setShowResolved] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  // Wave 26.10.6 — full-screen bug detail view (tap a bug to open).
  const [viewingBug, setViewingBug] = useState(null);
  const [savingDetail, setSavingDetail] = useState(false);
  const location = useLocation();
  const ref = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Close on outside click — but treat clicks inside any portal-rendered
  // Modal as "inside" so the bug panel doesn't get killed when the user
  // interacts with the Voice review modal (Use raw / Use refined).
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (e.target?.closest?.('[data-hv-modal-portal="true"]')) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  // Paste-from-clipboard support
  useEffect(() => {
    if (!open || tab !== "new") return;
    const onPaste = async (e) => {
      const items = e.clipboardData?.items || [];
      const files = [];
      for (const item of items) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        await ingestFiles(files);
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const ingestFiles = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    setErrMsg("");
    let failed = 0;
    try {
      const next = [];
      for (const f of Array.from(files).slice(0, 10)) {
        if (f.size > 20 * 1024 * 1024) {
          setErrMsg(`${f.name}: skipped (too large; 20MB max)`);
          failed++;
          continue;
        }
        try {
          const up = await uploadFile(f, { source: "bug_report" });
          next.push({
            upload_id: up.id,
            url: `/api/uploads/${up.id}`,
            filename: up.filename,
            content_type: up.content_type,
            size: up.size,
            kind: KIND_OF(up.content_type),
          });
        } catch (err) {
          failed++;
          const msg = err?.response?.data?.detail || err?.message || "upload failed";
          setErrMsg(`${f.name}: ${msg}`);
        }
      }
      setAttachments((prev) => [...prev, ...next].slice(0, 10));
      if (next.length === 0 && failed > 0) {
        setErrMsg((prev) => prev || `${failed} attachment${failed === 1 ? "" : "s"} failed — you can still submit the bug as text-only.`);
      }
    } finally {
      setUploading(false);
    }
  }, []);

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!reason.trim()) {
      setErrMsg("Describe the bug in a sentence (or tap the mic to dictate).");
      return;
    }
    setSending(true);
    setErrMsg("");
    try {
      await api.post("/hidro/bugs", {
        kind: "user_report",
        severity,
        surface: "global",
        route: location.pathname,
        user_reason: reason.trim(),
        attachments,
        voice_transcript: reason.trim(),
        console_errors: _consoleErrors.slice(-15).map((e) => `[${e.at}] ${e.line}`),
        user_agent: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 480) : null,
      });
      setDone(true);
      setReason("");
      setAttachments([]);
      setTimeout(() => { setDone(false); setTab("mine"); loadMine(); }, 1300);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Couldn't file the bug. Try again.");
    }
    setSending(false);
  };

  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      // Owner with scope="team" pulls the global stream; otherwise per-user.
      const url = isOwner && scope === "team" ? "/hidro/bugs?limit=200" : "/hidro/bugs/mine";
      const r = await api.get(url);
      setMyBugs(r.data?.items || []);
    } catch (_) {
      setMyBugs([]);
    } finally {
      setLoadingMine(false);
    }
  }, [isOwner, scope]);

  const loadBriefs = useCallback(async () => {
    if (!isOwner) return;
    setLoadingBriefs(true);
    try {
      const r = await api.get("/hidro/bugs/briefs");
      setBriefs(r.data?.items || []);
    } catch (_) {
      setBriefs([]);
    } finally {
      setLoadingBriefs(false);
    }
  }, [isOwner]);

  useEffect(() => {
    if (open && tab === "mine") loadMine();
  }, [open, tab, loadMine]);

  useEffect(() => {
    if (open && tab === "briefs") loadBriefs();
  }, [open, tab, loadBriefs]);

  // Reset selection when scope or tab changes.
  useEffect(() => {
    setSelected(new Set());
    setCompiledBrief(null);
    setCompileErr("");
  }, [scope, tab]);

  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelected(new Set(myBugs.map((b) => b.id)));
  }, [myBugs]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  const formatAsMarkdown = useCallback((b) => {
    const att = (b.attachments || [])
      .map((a, i) => `${i + 1}. [${a.filename || a.kind || "file"}](${a.url || ""})`)
      .join("\n");
    const errs = (b.console_errors || []).slice(0, 10).join("\n");
    return [
      `### Bug — ${b.severity?.toUpperCase() || "MEDIUM"}`,
      `**Reported:** ${b.created_at}`,
      `**By:** ${b.user_name || ""} (${b.user_role || ""})`,
      `**Route:** \`${b.route || "—"}\``,
      `**Status:** ${b.status || "new"}`,
      "",
      `**What happened:**`,
      b.user_reason || "—",
      ...(att ? ["", "**Attachments:**", att] : []),
      ...(errs ? ["", "**Recent console errors:**", "```", errs, "```"] : []),
      ...(b.user_agent ? ["", `**User agent:** \`${b.user_agent}\``] : []),
    ].join("\n");
  }, []);

  const compileWithHidro = useCallback(async (regenerate = false) => {
    if (!isOwner || selected.size === 0) return;
    setCompiling(true);
    setCompileErr("");
    setBriefCopied(false);
    try {
      const r = await api.post("/hidro/bugs/compile", {
        bug_ids: Array.from(selected),
        framing: "build_instructions",
        include_resolved: includeResolved,
        use_voice_transcripts: true,
        regenerate_seed: regenerate ? Math.random().toString(36).slice(2, 8) : null,
      });
      setCompiledBrief(r.data);
    } catch (e) {
      setCompileErr(
        e?.response?.data?.detail ||
          e?.message ||
          `${aiName} couldn't compile the brief. Try again in a moment.`,
      );
    } finally {
      setCompiling(false);
    }
  }, [isOwner, selected, includeResolved]);

  const copyRawMarkdown = useCallback(async () => {
    if (selected.size === 0) return;
    const chosen = myBugs.filter((b) => selected.has(b.id));
    const md = chosen.map(formatAsMarkdown).join("\n\n---\n\n");
    try {
      await navigator.clipboard.writeText(md);
      setBriefCopied(true);
      setTimeout(() => setBriefCopied(false), 1800);
    } catch (_) {
      window.prompt("Copy this:", md);
    }
  }, [myBugs, selected, formatAsMarkdown]);

  const copyBriefMarkdown = useCallback(async () => {
    if (!compiledBrief?.markdown) return;
    try {
      await navigator.clipboard.writeText(compiledBrief.markdown);
      setBriefCopied(true);
      setTimeout(() => setBriefCopied(false), 1800);
    } catch (_) {
      window.prompt("Copy this:", compiledBrief.markdown);
    }
  }, [compiledBrief]);

  const deleteBrief = useCallback(async (id) => {
    if (!isOwner) return;
    if (!window.confirm("Delete this saved brief?")) return;
    try {
      await api.delete(`/hidro/bugs/briefs/${id}`);
      setBriefs((prev) => prev.filter((b) => b.id !== id));
      if (compiledBrief?.id === id) setCompiledBrief(null);
    } catch (_) { /* noop */ }
  }, [isOwner, compiledBrief]);

  const visibleBugs = useMemo(() => {
    if (showResolved) return myBugs;
    return myBugs.filter((b) => b.status !== "resolved" && b.status !== "wontfix");
  }, [myBugs, showResolved]);
  const allSelected = visibleBugs.length > 0 && visibleBugs.every((b) => selected.has(b.id));

  // Wave 26.10 — bulk mark-resolved (owner-driven, but allowed for any
  // reporter on their own bugs since the existing PATCH endpoint
  // already enforces auth).
  const bulkResolve = useCallback(async () => {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      const results = await Promise.allSettled(
        ids.map((id) => api.patch(`/hidro/bugs/${id}`, { status: "resolved" })),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        // Don't expose the underlying detail — keep it generic on UI.
        toast.error(`Could not resolve ${failed} of ${ids.length}.`);
      } else {
        toast.success(`Marked ${ids.length} bug${ids.length === 1 ? "" : "s"} resolved.`);
      }
      // Optimistically remove resolved items from local state so the
      // user sees the list shrink immediately. A reload will sync.
      setMyBugs((prev) => prev.map((b) => (selected.has(b.id) ? { ...b, status: "resolved" } : b)));
      setSelected(new Set());
    } finally {
      setBulkBusy(false);
    }
  }, [selected, bulkBusy]);

  const copyMarkdown = useCallback(async (b) => {
    const md = formatAsMarkdown(b);
    try {
      await navigator.clipboard.writeText(md);
      setCopiedId(b.id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch (_) {
      window.prompt("Copy this:", md);
    }
  }, [formatAsMarkdown]);

  return (
    <div className="fixed bottom-24 right-4 z-[60] print:hidden md:bottom-16" data-testid="global-bug-button-wrap">
      {!open ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setTab("new"); }}
          title={`Spotted a ${aiName} bug or weird behaviour? Tell us with a screenshot, voice note, or a sentence.`}
          className="hv-bug-fab w-9 h-9 rounded-full bg-[#1a1d24]/85 backdrop-blur-sm border border-white/10 hover:border-rose-500/40 hover:text-rose-300 text-[#647087] shadow-lg flex items-center justify-center transition-colors"
          data-testid="global-bug-button"
        >
          <Bug className="w-4 h-4" />
        </button>
      ) : (
        <div
          ref={ref}
          className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-16 sm:left-auto sm:w-[460px] sm:max-h-[88vh] rounded-t-2xl sm:rounded-lg hv-card !p-0 shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: 'min(var(--hv-vvh, 90dvh), 90dvh)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="global-bug-panel"
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={async (e) => {
            e.preventDefault();
            await ingestFiles(e.dataTransfer?.files);
          }}
        >
          <div className="px-3 pt-3 pb-2 flex-1 min-h-0 overflow-y-auto sm:p-3">
          <div className="flex items-center gap-2 mb-2">
            <Bug className="w-3.5 h-3.5 text-rose-300" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-rose-300 font-bold">{aiName} bug reports</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto text-[#647087] hover:text-white"
              data-testid="global-bug-close"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          {/* Tabs */}
          <div className="flex border border-white/10 rounded-md overflow-hidden mb-3 text-[11px] uppercase tracking-wider">
            <button
              type="button"
              onClick={() => setTab("new")}
              className={`flex-1 px-2 py-1.5 ${tab === "new" ? "bg-rose-500/15 text-rose-300" : "text-[#647087] hover:text-white"}`}
              data-testid="global-bug-tab-new"
            >
              New report
            </button>
            <button
              type="button"
              onClick={() => setTab("mine")}
              className={`flex-1 px-2 py-1.5 ${tab === "mine" ? "bg-rose-500/15 text-rose-300" : "text-[#647087] hover:text-white"}`}
              data-testid="global-bug-tab-mine"
            >
              My reports
            </button>
            {isOwner && (
              <button
                type="button"
                onClick={() => setTab("briefs")}
                className={`flex-1 px-2 py-1.5 ${tab === "briefs" ? "bg-rose-500/15 text-rose-300" : "text-[#647087] hover:text-white"}`}
                data-testid="global-bug-tab-briefs"
              >
                <Sparkles className="w-2.5 h-2.5 inline mr-0.5" /> Briefs
              </button>
            )}
          </div>

          {tab === "new" ? (
            done ? (
              <div className="text-emerald-300 text-xs inline-flex items-center gap-1 py-2">
                <Check className="w-3.5 h-3.5" /> Filed. Switching to "My reports" so you can copy it…
              </div>
            ) : (
              <>
                <VoiceTextarea
                  value={reason}
                  onChange={setReason}
                  rows={4}
                  placeholder={`What did ${aiName} / the app do wrong? Tap the mic to dictate, or paste/drop a screenshot below.`}
                  context="bug_report"
                  testIdPrefix="global-bug-voice"
                  data-testid="global-bug-reason"
                />

                {/* Attachments */}
                {attachments.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 mt-2" data-testid="global-bug-attachments">
                    {attachments.map((a, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square rounded border border-white/10 overflow-hidden bg-[#0a0b0e] group"
                        data-testid={`global-bug-attachment-${idx}`}
                      >
                        {a.kind === "image" ? (
                          <img src={uploadUrl(a.upload_id)} alt={a.filename} className="w-full h-full object-cover" />
                        ) : a.kind === "video" ? (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-[#1FB6C1] uppercase tracking-wider">VID</div>
                        ) : a.kind === "audio" ? (
                          <div className="w-full h-full flex items-center justify-center"><Mic className="w-5 h-5 text-[#A78BFA]" /></div>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[10px] text-[#F59E0B] uppercase tracking-wider">FILE</div>
                        )}
                        <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[9px] bg-black/60 text-white truncate">{a.filename}</div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded bg-rose-500/40 hover:bg-rose-500/70 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
                          data-testid={`global-bug-attachment-remove-${idx}`}
                        >
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tools row */}
                <div className="flex flex-wrap items-center gap-1 mt-2">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={uploading}
                    className="hv-btn hv-btn-ghost text-[10px] px-2 py-1"
                    title="Take a screenshot or photo"
                    data-testid="global-bug-camera"
                  >
                    <Camera className="w-3 h-3" /> Capture
                  </button>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="hv-btn hv-btn-ghost text-[10px] px-2 py-1"
                    title="Upload screenshot, image, video, or document"
                    data-testid="global-bug-attach"
                  >
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
                    Attach
                  </button>
                  <span className="text-[9px] text-[#647087] hidden sm:inline">
                    or paste / drag-drop
                  </span>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    className="hidden"
                    onChange={(e) => ingestFiles(e.target.files)}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.log"
                    multiple
                    className="hidden"
                    onChange={(e) => ingestFiles(e.target.files)}
                  />
                </div>

                <div className="text-[10px] text-[#647087] mt-1.5">
                  Auto-attaching: <span className="font-mono">{location.pathname}</span>
                  {_consoleErrors.length > 0 && <> · {Math.min(_consoleErrors.length, 15)} recent console errors</>}
                </div>

                {errMsg && (
                  <div className="text-[10px] text-rose-300 mt-1 inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {errMsg}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-2">
                  <select
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    className="hv-input text-[11px] py-1 px-1.5 min-w-0 flex-1"
                    data-testid="global-bug-severity"
                  >
                    <option value="low">Low — annoyance</option>
                    <option value="medium">Medium — wrong output</option>
                    <option value="high">High — blocks me</option>
                    <option value="critical">Critical — broken / crash</option>
                  </select>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={sending || !reason.trim()}
                    className="hv-btn hv-btn-primary text-xs whitespace-nowrap shrink-0"
                    data-testid="global-bug-submit"
                  >
                    {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bug className="w-3 h-3" />}
                    File it
                  </button>
                </div>
              </>
            )
          ) : tab === "mine" ? (
            viewingBug ? (
              <BugDetailView
                bug={viewingBug}
                isOwner={isOwner}
                saving={savingDetail}
                onBack={() => setViewingBug(null)}
                onSave={async (patch) => {
                  setSavingDetail(true);
                  try {
                    await api.patch(`/hidro/bugs/${viewingBug.id}`, patch);
                    // Reload list so changes show up
                    const url = isOwner && scope === "team" ? "/hidro/bugs" : "/hidro/bugs/mine";
                    const { data } = await api.get(url);
                    const bugs = Array.isArray(data) ? data : (data?.bugs || []);
                    setMyBugs(bugs);
                    const fresh = bugs.find((b) => b.id === viewingBug.id);
                    if (fresh) setViewingBug(fresh); else setViewingBug(null);
                    toast.success("Saved.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not save.");
                  } finally {
                    setSavingDetail(false);
                  }
                }}
              />
            ) : (
              <MyReportsList
                loading={loadingMine}
                bugs={visibleBugs}
                onCopy={copyMarkdown}
                copiedId={copiedId}
                onBack={() => setTab("new")}
                onOpenBug={(b) => setViewingBug(b)}
                isOwner={isOwner}
                scope={scope}
                onScopeChange={setScope}
                selected={selected}
                toggleSelect={toggleSelect}
                selectAll={selectAll}
                clearSelection={clearSelection}
                allSelected={allSelected}
                compiling={compiling}
                compileErr={compileErr}
                onCompile={() => compileWithHidro(false)}
                onRegenerate={() => compileWithHidro(true)}
                onCopyRaw={copyRawMarkdown}
                compiledBrief={compiledBrief}
                onCloseBrief={() => setCompiledBrief(null)}
                onCopyBrief={copyBriefMarkdown}
                briefCopied={briefCopied}
                includeResolved={includeResolved}
                onIncludeResolvedChange={setIncludeResolved}
                showResolved={showResolved}
                onShowResolvedChange={setShowResolved}
                onBulkResolve={bulkResolve}
                bulkBusy={bulkBusy}
                hiddenResolvedCount={myBugs.length - visibleBugs.length}
              />
            )
          ) : (
            <BriefsTab
              loading={loadingBriefs}
              briefs={briefs}
              activeBrief={compiledBrief}
              onView={(b) => setCompiledBrief(b)}
              onCloseBrief={() => setCompiledBrief(null)}
              onCopyBrief={copyBriefMarkdown}
              briefCopied={briefCopied}
              onDelete={deleteBrief}
              onBack={() => setTab("mine")}
            />
          )}
          </div>
        </div>
      )}
    </div>
  );
}

function MyReportsList({
  loading, bugs, onCopy, copiedId, onBack, onOpenBug,
  isOwner, scope, onScopeChange,
  selected, toggleSelect, selectAll, clearSelection, allSelected,
  compiling, compileErr, onCompile, onRegenerate, onCopyRaw,
  compiledBrief, onCloseBrief, onCopyBrief, briefCopied,
  includeResolved, onIncludeResolvedChange,
  showResolved, onShowResolvedChange, onBulkResolve, bulkBusy, hiddenResolvedCount,
}) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { branding } = useBranding();
  const aiName = branding?.ai_name || "Hidro";
  if (loading) {
    return <div className="text-[#647087] text-xs py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading your reports…</div>;
  }

  // If a brief was just compiled, show the result panel front and centre.
  if (compiledBrief) {
    return (
      <BriefViewer
        brief={compiledBrief}
        onClose={onCloseBrief}
        onCopy={onCopyBrief}
        onRegenerate={onRegenerate}
        compiling={compiling}
        copied={briefCopied}
      />
    );
  }

  return (
    <div className="space-y-2" data-testid="global-bug-mine-list">
      {/* Owner-only scope toggle: own bugs vs full team stream. */}
      {isOwner && (
        <div className="flex border border-white/10 rounded-md overflow-hidden text-[10px] uppercase tracking-wider">
          <button
            type="button"
            onClick={() => onScopeChange("mine")}
            className={`flex-1 px-2 py-1 inline-flex items-center justify-center gap-1 ${scope === "mine" ? "bg-[#1FB6C1]/15 text-[#1FB6C1]" : "text-[#647087] hover:text-white"}`}
            data-testid="global-bug-scope-mine"
          >
            <UserIcon className="w-2.5 h-2.5" /> Mine
          </button>
          <button
            type="button"
            onClick={() => onScopeChange("team")}
            className={`flex-1 px-2 py-1 inline-flex items-center justify-center gap-1 ${scope === "team" ? "bg-[#1FB6C1]/15 text-[#1FB6C1]" : "text-[#647087] hover:text-white"}`}
            data-testid="global-bug-scope-team"
          >
            <Users className="w-2.5 h-2.5" /> Team ({bugs.length})
          </button>
        </div>
      )}

      {bugs.length === 0 ? (
        <div className="text-center py-6">
          <Bug className="w-7 h-7 mx-auto text-[#647087] mb-2 opacity-60" />
          <div className="text-xs text-[#a0aabf] mb-2">
            {showResolved
              ? (isOwner && scope === "team" ? "No bugs reported yet." : "You haven't filed any bugs yet.")
              : (hiddenResolvedCount > 0
                  ? `All ${hiddenResolvedCount} bug${hiddenResolvedCount === 1 ? " is" : "s are"} resolved. Toggle "Show resolved" to view.`
                  : (isOwner && scope === "team" ? "No bugs reported yet." : "You haven't filed any bugs yet."))}
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => onShowResolvedChange?.(!showResolved)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="global-bug-toggle-resolved-empty"
            >
              {showResolved ? "Hide resolved" : "Show resolved"}
            </button>
            <button onClick={onBack} className="hv-btn hv-btn-ghost text-xs" data-testid="global-bug-mine-back">
              <ChevronLeft className="w-3 h-3" /> Back to new report
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-[10px] text-[#647087] flex items-center justify-between gap-2">
            <span>
              {isOwner ? (
                <>Tick the bugs and tap <Sparkles className="inline w-2.5 h-2.5 text-[#1FB6C1]" /> to compile a build brief.</>
              ) : (
                <>Tap <Copy className="inline w-2.5 h-2.5" /> to copy a Markdown summary.</>
              )}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => onShowResolvedChange?.(!showResolved)}
                className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-white"
                data-testid="global-bug-toggle-resolved"
                title={showResolved ? "Hide resolved bugs from the list" : "Show resolved bugs in the list"}
              >
                {showResolved ? "Hide resolved" : `Show resolved${hiddenResolvedCount > 0 ? ` (${hiddenResolvedCount})` : ""}`}
              </button>
              <button
                type="button"
                onClick={allSelected ? clearSelection : selectAll}
                className="text-[10px] uppercase tracking-wider text-[#1FB6C1] hover:underline"
                data-testid="global-bug-select-all"
              >
                {allSelected ? "Clear" : "Select all"}
              </button>
            </div>
          </div>

          {bugs.map((b) => (
            <div
              key={b.id}
              className={`border rounded-md p-2 transition-colors ${
                selected.has(b.id)
                  ? "border-[#1FB6C1]/50 bg-[#1FB6C1]/5"
                  : "border-white/10 bg-white/[0.02] hover:border-[#1FB6C1]/30 cursor-pointer"
              }`}
              data-testid={`global-bug-mine-item-${b.id}`}
              onClick={(e) => {
                // Don't open detail when clicking on inner buttons/links
                if (e.target.closest('button, a, input, label')) return;
                onOpenBug?.(b);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenBug?.(b); } }}
            >
              <div className="flex items-start gap-2">
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => toggleSelect(b.id)}
                    className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      selected.has(b.id)
                        ? "bg-[#1FB6C1] border-[#1FB6C1] text-[#0a0b0e]"
                        : "border-white/20 hover:border-[#1FB6C1]/60"
                    }`}
                    data-testid={`global-bug-select-${b.id}`}
                    aria-label={selected.has(b.id) ? "Deselect" : "Select"}
                  >
                    {selected.has(b.id) && <Check className="w-2.5 h-2.5" strokeWidth={3} />}
                  </button>
                )}
                <SeverityChip severity={b.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{b.user_reason || "—"}</div>
                  <div className="text-[10px] text-[#647087] flex items-center gap-1.5 mt-0.5">
                    <span>{new Date(b.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <span className="font-mono truncate">{b.route || "—"}</span>
                    {isOwner && scope === "team" && b.user_name && (
                      <>
                        <span>·</span>
                        <span className="text-[#a0aabf]">{b.user_name}</span>
                      </>
                    )}
                  </div>
                  {(b.attachments || []).length > 0 && (
                    <div className="flex gap-1 mt-1.5 flex-wrap">
                      {(b.attachments || []).slice(0, 4).map((a, i) => (
                        <a
                          key={i}
                          href={uploadUrl(a.upload_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] px-1.5 py-0.5 rounded bg-[#1FB6C1]/10 text-[#1FB6C1] border border-[#1FB6C1]/30 inline-flex items-center gap-1"
                        >
                          {a.kind === "image" ? <Camera className="w-2.5 h-2.5" /> : a.kind === "audio" ? <Mic className="w-2.5 h-2.5" /> : <Clipboard className="w-2.5 h-2.5" />}
                          {a.filename || a.kind}
                        </a>
                      ))}
                      {(b.attachments || []).length > 4 && (
                        <span className="text-[10px] text-[#647087]">+{(b.attachments || []).length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(b)}
                  className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1 flex-shrink-0"
                  title="Copy this single bug as Markdown"
                  data-testid={`global-bug-copy-${b.id}`}
                >
                  {copiedId === b.id ? <Check className="w-3 h-3 text-emerald-300" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#647087]">
                <StatusChip status={b.status} />
                {(b.console_errors || []).length > 0 && (
                  <span>· {(b.console_errors || []).length} console errors captured</span>
                )}
              </div>
            </div>
          ))}

          <button onClick={onBack} className="hv-btn hv-btn-ghost text-xs w-full justify-center" data-testid="global-bug-mine-new">
            <ChevronRight className="w-3 h-3 rotate-180" /> File another bug
          </button>

          {/* Sticky compile bar (owner only, when ≥1 selected) */}
          {isOwner && selected.size > 0 && (
            <div
              className="sticky bottom-0 -mx-3 -mb-3 px-3 py-2 bg-[#0a0b0e]/95 backdrop-blur border-t border-[#1FB6C1]/30 flex flex-col gap-1.5"
              data-testid="global-bug-compile-bar"
            >
              {compileErr && (
                <div className="text-[10px] text-rose-300 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {compileErr}
                </div>
              )}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-[#1FB6C1] font-bold uppercase tracking-wider">
                  {selected.size} selected
                </span>
                <label className="text-[10px] text-[#647087] inline-flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeResolved}
                    onChange={(e) => onIncludeResolvedChange(e.target.checked)}
                    className="accent-[#1FB6C1] w-3 h-3"
                    data-testid="global-bug-include-resolved"
                  />
                  Include resolved
                </label>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-[10px] text-[#647087] hover:text-white ml-auto"
                  data-testid="global-bug-clear-selection"
                >
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={onCompile}
                  disabled={compiling}
                  className="hv-btn hv-btn-primary text-xs flex-1 justify-center"
                  data-testid="global-bug-compile"
                >
                  {compiling ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Compiling…</>
                  ) : (
                    <><Sparkles className="w-3 h-3" /> Compile with {aiName}</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onCopyRaw}
                  className="hv-btn hv-btn-ghost text-xs"
                  title="Copy selected bugs as raw Markdown (no AI)"
                  data-testid="global-bug-copy-raw"
                >
                  <Copy className="w-3 h-3" /> Raw
                </button>
                <button
                  type="button"
                  onClick={onBulkResolve}
                  disabled={bulkBusy}
                  className="hv-btn hv-btn-ghost text-xs"
                  title="Mark selected bugs resolved"
                  data-testid="global-bug-bulk-resolve"
                >
                  {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Resolve
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BriefViewer({ brief, onClose, onCopy, onRegenerate, compiling, copied, hideRegenerate = false }) {
  return (
    <div className="space-y-2" data-testid="global-bug-brief-viewer">
      <div className="flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 text-[#1FB6C1] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-[#1FB6C1] truncate" data-testid="global-bug-brief-title">
            {brief.title || "Build brief"}
          </div>
          <div className="text-[10px] text-[#647087]">
            {brief.bug_count} bug{brief.bug_count === 1 ? "" : "s"} · {new Date(brief.created_at).toLocaleString()}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[#647087] hover:text-white"
          data-testid="global-bug-brief-close"
        >
          <XIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      <pre
        className="border border-[#1FB6C1]/20 rounded-md p-2 bg-[#0a0b0e] text-[11px] leading-relaxed text-[#dde3ec] whitespace-pre-wrap max-h-[40vh] overflow-y-auto font-mono"
        data-testid="global-bug-brief-markdown"
      >
        {brief.markdown}
      </pre>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onCopy}
          className="hv-btn hv-btn-primary text-xs flex-1 justify-center"
          data-testid="global-bug-brief-copy"
        >
          {copied ? (
            <><Check className="w-3 h-3" /> Copied!</>
          ) : (
            <><Copy className="w-3 h-3" /> Copy build instructions</>
          )}
        </button>
        {!hideRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            disabled={compiling}
            className="hv-btn hv-btn-ghost text-xs"
            title="Regenerate with a different framing"
            data-testid="global-bug-brief-regenerate"
          >
            {compiling ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        )}
      </div>

      <div className="text-[10px] text-[#647087] italic">
        {hideRegenerate
          ? "Saved brief — re-grab the markdown anytime."
          : "Saved to your Briefs tab — re-grab it any time."}
      </div>
    </div>
  );
}

function BriefsTab({ loading, briefs, activeBrief, onView, onCloseBrief, onCopyBrief, briefCopied, onDelete, onBack }) {
  if (loading) {
    return <div className="text-[#647087] text-xs py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading briefs…</div>;
  }
  // If user clicked into a brief from the list, swap the panel to the
  // viewer so they can read + copy without a tab switch.
  if (activeBrief) {
    return (
      <BriefViewer
        brief={activeBrief}
        onClose={onCloseBrief}
        onCopy={onCopyBrief}
        onRegenerate={() => { /* regenerate not offered from history view */ }}
        compiling={false}
        copied={briefCopied}
        hideRegenerate
      />
    );
  }
  if (!briefs.length) {
    return (
      <div className="text-center py-6" data-testid="global-bug-briefs-empty">
        <FileText className="w-7 h-7 mx-auto text-[#647087] mb-2 opacity-60" />
        <div className="text-xs text-[#a0aabf] mb-2">No saved briefs yet.</div>
        <div className="text-[10px] text-[#647087] mb-3 max-w-[260px] mx-auto">
          In "My reports", select a few bugs and tap <Sparkles className="inline w-2.5 h-2.5 text-[#1FB6C1]" /> Compile to generate a paste-ready build brief.
        </div>
        <button onClick={onBack} className="hv-btn hv-btn-ghost text-xs" data-testid="global-bug-briefs-back">
          <ChevronLeft className="w-3 h-3" /> Pick bugs
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="global-bug-briefs-list">
      <div className="text-[10px] text-[#647087]">
        Tap a brief to re-view and copy. Briefs persist across sessions.
      </div>
      {briefs.map((b) => (
        <div
          key={b.id}
          className="border rounded-md p-2 transition-colors cursor-pointer border-white/10 bg-white/[0.02] hover:border-[#1FB6C1]/30"
          data-testid={`global-bug-brief-item-${b.id}`}
          onClick={() => onView(b)}
        >
          <div className="flex items-start gap-2">
            <Sparkles className="w-3 h-3 text-[#1FB6C1] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{b.title || "Build brief"}</div>
              <div className="text-[10px] text-[#647087] truncate mt-0.5">
                {b.summary_short || `${b.bug_count} bug(s) compiled`}
              </div>
              <div className="text-[10px] text-[#647087] mt-1">
                {new Date(b.created_at).toLocaleString()} · {b.bug_count} bug{b.bug_count === 1 ? "" : "s"}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(b.id); }}
              className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1 text-rose-300 flex-shrink-0"
              title="Delete brief"
              data-testid={`global-bug-brief-delete-${b.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SeverityChip({ severity }) {
  const map = {
    low: { color: "#647087", label: "LOW" },
    medium: { color: "#F59E0B", label: "MED" },
    high: { color: "#F43F5E", label: "HIGH" },
    critical: { color: "#DC2626", label: "CRIT" },
  };
  const s = map[severity] || map.medium;
  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border flex-shrink-0"
      style={{ borderColor: `${s.color}55`, background: `${s.color}1a`, color: s.color }}
    >
      {s.label}
    </span>
  );
}

function StatusChip({ status }) {
  const map = {
    new: "rose",
    triaged: "amber",
    fixing: "[#1FB6C1]",
    resolved: "emerald",
    wontfix: "[#647087]",
  };
  return (
    <span className={`text-[9px] uppercase tracking-wider text-${map[status] || "rose"}-300`}>
      {(status || "new").toUpperCase()}
    </span>
  );
}


/**
 * Wave 26.10.6 — Full-screen bug detail panel.
 *
 * Mounted in place of the list when a row is tapped. Owner/admin can edit
 * status + severity + notes inline. Everyone can read full transcript,
 * console errors and attachments.
 */
function BugDetailView({ bug, isOwner, saving, onBack, onSave }) {
  const [status, setStatus] = useState(bug.status || "new");
  const [severity, setSeverity] = useState(bug.severity || "medium");
  const [notes, setNotes] = useState(bug.notes || "");
  // 2026-02-15 — lightbox state. Bug fix (Dirk): tapping an attachment
  // previously opened it via `target="_blank"` which on iOS Safari PWA
  // standalone mode shows the image fullscreen with NO back / close /
  // swipe affordance — user had to kill the app. Now we route through
  // MediaLightbox which has a proper X button + Esc + swipe navigation.
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const imageAttachments = (bug.attachments || []).filter((a) => a.kind === "image");
  const lightboxItems = imageAttachments.map((a) => ({
    src: uploadUrl(a.upload_id),
    kind: "image",
    caption: a.filename,
  }));
  const dirty = status !== (bug.status || "new")
    || severity !== (bug.severity || "medium")
    || (notes || "") !== (bug.notes || "");

  const save = () => {
    const patch = {};
    if (status !== (bug.status || "new")) patch.status = status;
    if (severity !== (bug.severity || "medium")) patch.severity = severity;
    if ((notes || "") !== (bug.notes || "")) patch.notes = notes;
    onSave(patch);
  };

  return (
    <div className="space-y-3" data-testid="global-bug-detail-view">
      {/* Sticky back bar */}
      <div className="-mx-3 -mt-3 px-3 py-2 border-b border-white/10 bg-[#0a0b0e]/95 backdrop-blur sticky top-0 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="global-bug-detail-back"
        >
          <ChevronLeft className="w-3 h-3" /> Back
        </button>
        <SeverityChip severity={bug.severity} />
        <StatusChip status={bug.status} />
        <span className="text-[10px] text-[#647087] truncate ml-auto">
          {new Date(bug.created_at).toLocaleString()}
        </span>
      </div>

      {/* Reporter + route */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#647087]">Reporter</div>
        <div className="text-xs">{bug.user_name || bug.user_email || "—"} <span className="text-[#647087]">· {bug.user_role || "user"}</span></div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#647087]">Route</div>
        <div className="text-xs font-mono break-all">{bug.route || "—"}</div>
      </div>

      {/* Reason */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">What happened</div>
        <div className="text-xs whitespace-pre-wrap p-2 border border-white/10 rounded bg-white/[0.02]" data-testid="global-bug-detail-reason">
          {bug.user_reason || "—"}
        </div>
      </div>

      {/* Attachments */}
      {(bug.attachments || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Attachments</div>
          <div className="flex gap-1 flex-wrap">
            {(bug.attachments || []).map((a, i) => {
              const isImage = a.kind === "image";
              const imgIdx = isImage
                ? imageAttachments.findIndex((x) => x.upload_id === a.upload_id)
                : -1;
              const commonCls = "text-[10px] px-1.5 py-0.5 rounded bg-[#1FB6C1]/10 text-[#1FB6C1] border border-[#1FB6C1]/30 inline-flex items-center gap-1";
              if (isImage && imgIdx >= 0) {
                // Open in MediaLightbox instead of new tab.
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setLightboxIdx(imgIdx)}
                    className={commonCls}
                    data-testid={`global-bug-detail-attachment-${i}`}
                  >
                    <Camera className="w-2.5 h-2.5" />
                    {a.filename || a.kind}
                  </button>
                );
              }
              // Non-image attachments fall back to a download link, but
              // open in same tab (download attribute) so iOS PWA still
              // has a back gesture.
              return (
                <a
                  key={i}
                  href={uploadUrl(a.upload_id)}
                  download={a.filename || true}
                  className={commonCls}
                  data-testid={`global-bug-detail-attachment-${i}`}
                >
                  {a.kind === "audio" ? <Mic className="w-2.5 h-2.5" /> : <Clipboard className="w-2.5 h-2.5" />}
                  {a.filename || a.kind}
                </a>
              );
            })}
          </div>
          <MediaLightbox
            items={lightboxItems}
            index={lightboxIdx}
            onChange={setLightboxIdx}
            onClose={() => setLightboxIdx(-1)}
          />
        </div>
      )}

      {/* Console errors */}
      {(bug.console_errors || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">
            Console errors ({bug.console_errors.length})
          </div>
          <div className="text-[10px] font-mono p-2 border border-rose-400/30 bg-rose-500/5 rounded max-h-48 overflow-y-auto whitespace-pre-wrap break-all">
            {bug.console_errors.slice(0, 30).map((c, i) => (
              <div key={i} className="text-rose-200 mb-1">{typeof c === "string" ? c : (c.message || JSON.stringify(c))}</div>
            ))}
          </div>
        </div>
      )}

      {/* User agent */}
      {bug.user_agent && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">User agent</div>
          <div className="text-[10px] text-[#a0aabf] font-mono break-all">{bug.user_agent}</div>
        </div>
      )}

      {/* Owner edit area */}
      {isOwner ? (
        <div className="border-t border-white/10 pt-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1]">Triage</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] text-[#647087] mb-0.5">Status</div>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="hv-input text-xs py-1 px-1.5 w-full"
                data-testid="global-bug-detail-status"
              >
                <option value="new">New</option>
                <option value="triaged">Triaged</option>
                <option value="fixing">Fixing</option>
                <option value="resolved">Resolved</option>
                <option value="wontfix">Won't fix</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] text-[#647087] mb-0.5">Severity</div>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="hv-input text-xs py-1 px-1.5 w-full"
                data-testid="global-bug-detail-severity"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[#647087] mb-0.5">Triage notes</div>
            <DictTextarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="hv-input text-xs w-full"
              placeholder="Notes for the team / RCA / next step…"
              data-testid="global-bug-detail-notes"
            />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="global-bug-detail-save"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save
            </button>
          </div>
        </div>
      ) : (
        bug.notes && (
          <div className="border-t border-white/10 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Triage notes</div>
            <div className="text-xs whitespace-pre-wrap text-[#a0aabf]">{bug.notes}</div>
          </div>
        )
      )}
    </div>
  );
}
